import { createContext, useContext, ReactNode, useMemo, useState, useCallback } from 'react';
import { DashboardGlobalData } from '../types';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

// ============================================================================
// Dashboard Context
// ============================================================================

interface DashboardContextValue {
  /** Global dashboard data */
  data: DashboardGlobalData | null;
  /** Whether data is loading */
  isLoading: boolean;
  /** Error if data fetch failed */
  error: Error | null;
  /** Refetch all data */
  refetch: () => void;
  /** Last fetch timestamp */
  lastFetched: Date | null;
}

const DashboardContext = createContext<DashboardContextValue | undefined>(undefined);

// ============================================================================
// DashboardProvider Component
// ============================================================================

export interface DashboardProviderProps {
  /** Child components */
  children: ReactNode;
  /** Initial data (for demo/testing) */
  initialData?: DashboardGlobalData;
  /** Custom data fetcher */
  fetchData?: () => Promise<DashboardGlobalData>;
}

export function DashboardProvider({
  children,
  initialData,
  fetchData,
}: DashboardProviderProps) {
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  const [data, setData] = useState<DashboardGlobalData | null>(initialData || null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [lastFetched, setLastFetched] = useState<Date | null>(
    initialData ? new Date() : null
  );

  const refetch = useCallback(async () => {
    if (!fetchData) {
      // If no fetch function provided, just use initial data
      if (initialData) {
        setData(initialData);
        setLastFetched(new Date());
      }
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const newData = await fetchData();
      setData(newData);
      setLastFetched(new Date());
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch dashboard data'));
    } finally {
      setIsLoading(false);
    }
  }, [fetchData, initialData]);

  const value = useMemo<DashboardContextValue>(
    () => ({
      data,
      isLoading,
      error,
      refetch,
      lastFetched,
    }),
    [data, isLoading, error, refetch, lastFetched]
  );

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
}

// ============================================================================
// useDashboardContext Hook
// ============================================================================

export function useDashboardContext(): DashboardContextValue {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboardContext must be used within a DashboardProvider');
  }
  return context;
}

// ============================================================================
// useDashboardData Hook
// ============================================================================

export interface UseDashboardDataReturn {
  /** Global dashboard data */
  data: DashboardGlobalData | null;
  /** Whether data is loading */
  isLoading: boolean;
  /** Error if data fetch failed */
  error: Error | null;
  /** Refetch all data */
  refetch: () => void;
}

/**
 * Hook to access global dashboard data.
 * Must be used within a DashboardProvider.
 */
export function useDashboardData(): UseDashboardDataReturn {
  const { data, isLoading, error, refetch } = useDashboardContext();
  return { data, isLoading, error, refetch };
}

// ============================================================================
// useWidgetData Hook
// ============================================================================

export interface UseWidgetDataOptions<T> {
  /** Whether to enable auto-fetching */
  enabled?: boolean;
  /** Refresh interval in milliseconds */
  refreshInterval?: number;
  /** Initial data */
  initialData?: T;
}

export interface UseWidgetDataReturn<T> {
  /** Widget-specific data */
  data: T | null;
  /** Whether data is loading */
  isLoading: boolean;
  /** Error if fetch failed */
  error: Error | null;
  /** Refetch data */
  refetch: () => void;
}

/**
 * Hook for widgets that fetch their own data.
 * Provides loading state, error handling, and optional auto-refresh.
 */
export function useWidgetData<T>(
  fetchFn: () => Promise<T>,
  options: UseWidgetDataOptions<T> = {}
): UseWidgetDataReturn<T> {
  const { enabled = true, refreshInterval, initialData } = options;

  const [data, setData] = useState<T | null>(initialData || null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (!enabled) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await fetchFn();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch widget data'));
    } finally {
      setIsLoading(false);
    }
  }, [fetchFn, enabled]);

  // Initial fetch
  useMemo(() => {
    if (enabled && !initialData) {
      refetch();
    }
  }, [enabled, initialData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh
  useMemo(() => {
    if (!refreshInterval || !enabled) return;

    const interval = setInterval(refetch, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval, enabled, refetch]);

  return { data, isLoading, error, refetch };
}

export default DashboardProvider;
